import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * "Follow-up due" popup state.
 *
 * SERVER-DRIVEN. A follow-up's due moment is decided by the backend sweep
 * (jobs/workers/followup-reminder.worker.ts), which pushes `followup:due` over
 * the websocket to that follow-up's personal-scope owners. The client just
 * queues what arrives and shows one modal at a time.
 *
 * It used to be the other way round: scheduling a follow-up pushed it into a
 * local `scheduled` array that a setInterval polled. That could only ever fire
 * for the person who created the follow-up, in the tab they created it in, if
 * they never reloaded — which is why the whole thing shipped behind a hardcoded
 * `NOTIFICATIONS_ENABLED = false`. Don't reintroduce client-side scheduling:
 * the server push works across devices, reloads, and assignees.
 */
export interface DueFollowUp {
  /** FollowUpDate row id — the dedupe key. */
  id: string;
  leadId: string | number;
  childId?: string | number | null;
  leadName: string;
  leadInitials: string;
  /** ISO string of the moment the follow-up was due. */
  date: string;
  note: string;
  stage?: string;
  phone?: string;
  phoneCountryCode?: string | null;
  /**
   * How many OTHER follow-ups came due for this user in the same server tick.
   * The server coalesces per recipient — one alert naming the earliest-due lead
   * — so this is the honest "+N more" count. It is NOT `queue.length`: the queue
   * only holds separate ticks.
   */
  alsoDueCount?: number;
}

export interface FollowUpNotificationState {
  /** Due follow-ups waiting behind the one on screen. */
  queue: DueFollowUp[];
  /** Currently displayed (null = popup closed). */
  active: DueFollowUp | null;
  /** Ids already shown this session — a reconnect must not re-pop them. */
  seen: string[];
}

const initialState: FollowUpNotificationState = {
  queue: [],
  active: null,
  seen: [],
};

/** Cap on `seen` so a long-lived tab can't grow it without bound. */
const SEEN_CAP = 200;

function promoteNext(state: FollowUpNotificationState) {
  if (state.active) return;
  if (state.queue.length > 0) {
    state.active = state.queue[0];
    state.queue = state.queue.slice(1);
  }
}

const followUpNotificationSlice = createSlice({
  name: "followUpNotification",
  initialState,
  reducers: {
    /** A `followup:due` socket push landed. */
    pushDueFollowUp(state, action: PayloadAction<DueFollowUp>) {
      const incoming = action.payload;
      if (!incoming?.id) return;
      // Dedupe against everything we've shown or are about to show. The server
      // stamps `remindedAt` so it only pushes once, but a socket reconnect can
      // replay and the popup must not stutter.
      if (state.seen.includes(incoming.id)) return;
      if (state.active?.id === incoming.id) return;
      if (state.queue.some((f) => f.id === incoming.id)) return;

      state.seen.push(incoming.id);
      if (state.seen.length > SEEN_CAP) {
        state.seen = state.seen.slice(-SEEN_CAP);
      }
      state.queue.push(incoming);
      promoteNext(state);
    },
    dismissFollowUp(state) {
      state.active = null;
      promoteNext(state);
    },
    /**
     * Put a reminder back after an action on it FAILED (a postpone whose PATCH
     * was rejected). Deliberately bypasses the `seen` dedupe that
     * `pushDueFollowUp` enforces: the id has already been seen, but the server
     * has stamped `remindedAt` and will never re-send it, so refusing to
     * re-queue here would lose the reminder for good.
     */
    requeueFollowUp(state, action: PayloadAction<DueFollowUp>) {
      const item = action.payload;
      if (!item?.id) return;
      if (state.active?.id === item.id) return;
      if (state.queue.some((f) => f.id === item.id)) return;
      state.queue.unshift(item);
      promoteNext(state);
    },
    updateActiveNote(state, action: PayloadAction<string>) {
      if (state.active) {
        state.active.note = action.payload;
      }
    },
    clearActive(state) {
      state.active = null;
      promoteNext(state);
    },
  },
});

export const {
  pushDueFollowUp,
  dismissFollowUp,
  requeueFollowUp,
  updateActiveNote,
  clearActive,
} = followUpNotificationSlice.actions;

export default followUpNotificationSlice.reducer;
